import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { tokens, setSessionLostHandler } from '../api/client.js';
import { authApi } from '../api/endpoints.js';

const AuthContext = createContext(null);

/** Roles allowed into the console at all. The app is for employees. */
const CONSOLE_ROLES = ['OWNER', 'ADMIN', 'HR', 'MANAGER'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => tokens.user);
  const [employee, setEmployee] = useState(null);
  const [permissions, setPermissions] = useState(() => tokens.user?.permissions ?? []);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async ({ callServer = true } = {}) => {
    if (callServer && tokens.refresh) {
      await authApi.logout(tokens.refresh).catch(() => {});
    }
    tokens.clear();
    setUser(null);
    setEmployee(null);
    setPermissions([]);
  }, []);

  // The API client calls this when a refresh fails, so an expired session
  // lands on the login screen instead of a wall of failed requests.
  useEffect(() => {
    setSessionLostHandler(() => {
      setUser(null);
      setEmployee(null);
      setPermissions([]);
    });
  }, []);

  /** Re-reads the session from the server so a role change lands immediately. */
  const refreshUser = useCallback(async () => {
    if (!tokens.access) {
      setLoading(false);
      return;
    }
    try {
      const data = await authApi.me();
      setUser(data.user);
      setEmployee(data.employee);
      setPermissions(data.permissions ?? []);
      tokens.set({ user: data.user });
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const signIn = useCallback(async (identifier, password) => {
    const data = await authApi.login(identifier, password);

    if (!CONSOLE_ROLES.includes(data.user.role)) {
      // Do not leave a usable session lying around for a rejected role.
      tokens.clear();
      const err = new Error('This console is for administrators. Please use the Catchu HR mobile app.');
      err.code = 'ROLE_NOT_ALLOWED';
      throw err;
    }

    tokens.set(data);
    setUser(data.user);
    setPermissions(data.user.permissions ?? []);

    // Fills in the employee record and the authoritative permission list.
    await refreshUser();
    return data.user;
  }, [refreshUser]);

  const value = useMemo(() => {
    const isOwner = user?.role === 'OWNER';

    return {
      user,
      employee,
      permissions,
      loading,
      isAuthenticated: Boolean(user),
      signIn,
      signOut,
      refreshUser,

      /** The owner implicitly holds everything, including future permissions. */
      can: (permission) => isOwner || permissions.includes(permission),
      canAny: (...list) => isOwner || list.some((p) => permissions.includes(p)),
      hasRole: (...roles) => roles.includes(user?.role),
    };
  }, [user, employee, permissions, loading, signIn, signOut, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
